// CB:BEGIN admin-debug-panel
/**
 * AdminDebugPanel
 *
 * Collapsible panel for admins that surfaces session context, impersonation
 * state, and environment info. Useful when debugging user-specific issues.
 */
import React, { useState, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { useImpersonation } from '../../context/ImpersonationContext';
import { getApiBaseUrl, getApiUrl } from '../../lib/getApiUrl';

const AdminDebugPanel: React.FC = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { isImpersonating, impersonatedUser, impersonationToken } = useImpersonation();
  const [open, setOpen] = useState(false);
  const [dbInfo, setDbInfo] = useState<{ id: string; role: string; email: string } | null>(null);
  const [dbLoading, setDbLoading] = useState(false);
  const [lastErr, setLastErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (!open || dbInfo) return;
    setDbLoading(true);
    getToken()
      .then(token =>
        fetch(getApiUrl('/api/me'), {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        })
      )
      .then(r => r.json())
      .then(data => setDbInfo({ id: data.id, role: data.role, email: data.email }))
      .catch(e => setLastErr(String(e)))
      .finally(() => setDbLoading(false));
  }, [open, dbInfo, getToken]);

  const copy = (label: string, value: string) => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  const Row: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ width: 160, flexShrink: 0, fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
      <span
        style={{ fontFamily: 'monospace', fontSize: 12, color: '#0f172a', wordBreak: 'break-all', cursor: value ? 'copy' : 'default' }}
        title={value ? 'Click to copy' : undefined}
        onClick={() => value && copy(label, value)}
      >
        {value || <span style={{ color: '#cbd5e1' }}>—</span>}
        {copied === label && <span style={{ color: '#22c55e', marginLeft: 8 }}>✓ copied</span>}
      </span>
    </div>
  );

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 16px',
          background: open ? '#1e293b' : '#f1f5f9',
          color: open ? '#e2e8f0' : '#475569',
          border: '1.5px solid',
          borderColor: open ? '#334155' : '#e2e8f0',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <span style={{ fontSize: 11 }}>🛠</span> Debug Info {open ? '▲' : '▼'}
      </button>

      {open && (
        <div style={{
          marginTop: 8,
          background: '#fff',
          border: '1.5px solid #e2e8f0',
          borderRadius: 12,
          padding: '16px 20px',
          maxWidth: 680,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 10 }}>CLERK SESSION</div>
          <Row label="Clerk User ID" value={user?.id} />
          <Row label="Clerk Email" value={user?.primaryEmailAddress?.emailAddress} />
          <Row label="Clerk Username" value={user?.username} />

          <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginTop: 16, marginBottom: 10 }}>DB IDENTITY {dbLoading && <span style={{ fontWeight: 400, color: '#94a3b8' }}>(loading…)</span>}</div>
          {dbInfo ? (
            <>
              <Row label="DB User ID" value={dbInfo.id} />
              <Row label="DB Role" value={dbInfo.role} />
              <Row label="DB Email" value={dbInfo.email} />
            </>
          ) : !dbLoading && (
            <div style={{ fontSize: 13, color: '#94a3b8' }}>Not loaded — expand to trigger fetch.</div>
          )}

          <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginTop: 16, marginBottom: 10 }}>ENVIRONMENT</div>
          <Row label="API Base URL" value={getApiBaseUrl()} />
          <Row label="Frontend URL" value={window.location.origin} />
          <Row label="Mode" value={import.meta.env.MODE} />

          {isImpersonating && impersonatedUser && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#b45309', marginTop: 16, marginBottom: 10 }}>IMPERSONATION (active)</div>
              <Row label="Target User ID" value={impersonatedUser.id} />
              <Row label="Target Email" value={impersonatedUser.email} />
              <Row label="Target Role" value={impersonatedUser.role} />
              <Row label="Target Name" value={impersonatedUser.displayName} />
              <Row label="Homeowner ID" value={impersonatedUser.homeownerId} />
              <Row label="Contractor ID" value={impersonatedUser.contractorId} />
              <Row label="PM Profile ID" value={impersonatedUser.pmProfileId} />
              <Row label="Session Token" value={impersonationToken ? `${impersonationToken.slice(0, 20)}…` : null} />
            </>
          )}

          {lastErr && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#dc2626' }}>
              Last error: {lastErr}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDebugPanel;
// CB:END admin-debug-panel
